import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { FamilyCodes } from 'src/app/core/_enums/family.code';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { ProductFamilyType } from 'src/app/core/_enums/product.family.type';
import { ResourceTypeCodes } from 'src/app/core/_enums/resource.type.codes';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { WorkUnityCode } from 'src/app/core/_enums/work.unity.code';
import { CombustibleFormula } from 'src/app/core/_models/combustibleFormula';
import { Energie } from 'src/app/core/_models/energie';
import { EnergyProfile, EnergyProfileCombustible } from 'src/app/core/_models/energyProfile';
import { Family } from 'src/app/core/_models/family';
import { Formula } from 'src/app/core/_models/formula';
import { FormulaComposition } from 'src/app/core/_models/formulaComposition';
import { Industrie } from 'src/app/core/_models/industrie';
import { Product } from 'src/app/core/_models/product';
import { ProjectType, compare } from 'src/app/core/_models/projectType';
import { Transport } from 'src/app/core/_models/transport';
import { Unity } from 'src/app/core/_models/unity';
import { UserEntity } from 'src/app/core/_models/userEntity';
import { Version } from 'src/app/core/_models/version';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { AuthService } from 'src/app/core/_services/auth.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { FamilyService } from 'src/app/core/_services/family.service';
import { IndustrieService } from 'src/app/core/_services/industrie.service';
import { ProductFormulaService } from 'src/app/core/_services/product.formula.service';
import { ProjectTypeService } from 'src/app/core/_services/projectTypes.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { positiveIfCondition, requiredIfCondition, validateProjectTypeRequired } from 'src/app/core/_utils/validation';
import { StoreSelect, UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { environment } from 'src/environments/environment';
import { DialogAddCompositionComponent } from '../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogGenerateFdesComponent } from '../shared/dialog-generate-fdes/dialog-generate-fdes.component';
import { FdesEligibility, FdesGeneration } from 'src/app/core/_models/fdes';
import { FdesService, fdesDocumentKind, saveFdesFile } from 'src/app/core/_services/fdes.service';
import { DialogDisplayCeuComponent } from '../shared/dialog-display-ceu/dialog-display-ceu/dialog-display-ceu.component';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogEditCombustibleFormulaComponent } from '../shared/dialog-edit-combustible-formula/dialog-edit-combustible-formula.component';
import { DialogEditCompositionComponent } from '../shared/dialog-edit-composition/dialog-edit-composition.component';
import { DialogEditRatioComponent } from '../shared/dialog-edit-ratio/dialog-edit-ratio.component';
import { DialogLeaveComponent } from '../shared/dialog-leave/dialog-leave.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { DialogSelectEuShareComponent } from '../shared/dialog-select-eu-share/dialog-select-eu-share.component';

@Component({
  selector: 'app-formula',
  templateUrl: './formula.component.html',
  styleUrls: ['./formula.component.scss']
})
export class FormulaComponent implements OnInit {
  currentItem!: Formula;

  // Ce que le serveur autorise sur la FDES de cette formule : les règles d'accès (#885) ne sont pas
  // rejouées ici, l'écran les demande à chaque chargement de formule.
  private fdesEligibility?: FdesEligibility;

  // Dernière FDES produite pour la formule. Elle a son emplacement propre, à côté du justificatif, et ne
  // le remplace pas (décision PO du 2026-07-29). Reste nulle tant que le PDF n'est pas produit (SEV-851).
  latestFdes?: FdesGeneration;
  // Tant que l'historique n'est pas revenu, la carte ne sait pas s'il y a une FDES à consulter : annoncer
  // « aucune FDES » entre-temps ferait clignoter un message faux.
  fdesHistoryLoaded = false;
  energyProfile: EnergyProfile | null = null;
  // Vrai quand le calcul de la version affichée lit le mix de l'usine (formule RdF 2025) : la formule affiche alors
  // ce mix en lecture seule. Sinon elle garde son propre %, modifiable, que lisent aussi les versions plus anciennes.
  usesPlantMix = false;
  private loadedIndustryId?: string;
  private storedCombustibles?: { industryId: string; combustibles: CombustibleFormula[] };

  // app-fdes-thermal-card takes flat display values — map the energy-profile response onto them
  get thermalConso(): string {
    return this.energyProfile?.consoTotal != null ? String(this.energyProfile.consoTotal) : '';
  }

  // Combustibles utilisés du mix — une ligne par combustible dans la carte (nom, part, FE).
  get thermalCombustibles(): EnergyProfileCombustible[] {
    return (this.energyProfile?.combustibles || []).filter((c) => (c.usagePct ?? 0) > 0);
  }

  // Arrondi 2 décimales à l'affichage (filet de sécurité si une valeur en BDD n'est pas déjà arrondie).
  round2(v: number | string | null | undefined): string {
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    if (!isFinite(n)) return v == null ? '' : String(v);
    return (Math.round(n * 100) / 100).toString();
  }

  get canGenerateFdes(): boolean {
    return !!this.fdesEligibility?.canGenerate;
  }

  get canViewFdes(): boolean {
    return !!this.fdesEligibility?.canView;
  }

  // Sans génération possible, la carte annonce l'adhésion perdue quand c'est elle qui bloque ; un profil en
  // lecture seule (bureau d'études) n'a lui rien à apprendre de plus que l'accès à l'historique, et rien à
  // consulter tant qu'aucune FDES n'a été produite.
  get fdesDescriptionKey(): string {
    if (this.canGenerateFdes) return 'formula_fdes_generate_desc';
    if (!this.fdesEligibility?.ownerIsRdfAdherent) return 'formula_fdes_not_adherent_desc';
    return this.latestFdes ? 'formula_fdes_history_desc' : 'formula_fdes_none_desc';
  }

  // Le bouton ouvre l'historique : sans génération à consulter et sans droit d'en produire, il n'ouvrirait
  // qu'une popup vide.
  get showFdesButton(): boolean {
    return this.canGenerateFdes || !!this.latestFdes;
  }

  async openGenerateFdes(): Promise<void> {
    // La FDES est calculée sur la formule enregistrée : des modifications en cours n'y figureraient pas.
    const canSave = this.manageProducts && this.currentItem?.canUpdate === true;
    if (this.canGenerateFdes && this.unSaved && canSave && !(await this.clickSave(this.currentItem))) {
      return;
    }

    const industry = this.listIndustries?.find((i) => i.resourceId === this.form.value.industryId);
    const ref = this.dialog.open(DialogGenerateFdesComponent, {
      panelClass: 'fdes-dialog-panel',
      autoFocus: false,
      data: {
        formulaId: this.currentItem?.resourceId,
        formulaName: this.currentItem?.displayName,
        industryName: industry?.displayName,
        proofFileName: this.form.value.fileName,
        canGenerate: this.canGenerateFdes,
        ownerIsRdfAdherent: !!this.fdesEligibility?.ownerIsRdfAdherent,
        needsDeclarantAddress: !!this.fdesEligibility?.needsDeclarantAddress
      }
    });
    // Une FTP déposée depuis la popup est le justificatif de la formule : on resynchronise le formulaire.
    ref.afterClosed().subscribe((res) => {
      if (res?.fileName) {
        this.form.get('fileName')?.setValue(res.fileName);
        this.form.get('fileDescription')?.setValue(res.fileDescription);
      }
      // Une génération a pu avoir lieu pendant l'ouverture : l'emplacement FDES doit suivre.
      this.loadLatestFdes();
    });
  }

  // Profil AI, membre de l'organisation propriétaire, et adhésion Routes de France pour générer : le
  // serveur applique le même contrôle que la génération elle-même, l'écran ne fait que le lui demander.
  private resolveFdesEligibility(): void {
    this.fdesEligibility = undefined;
    const formulaId = this.currentItem?.resourceId;
    if (!formulaId) {
      return;
    }
    this.fdesService.getEligibility(formulaId).subscribe({
      next: (eligibility) => {
        this.fdesEligibility = eligibility;
        this.loadLatestFdes();
      },
      error: () => (this.fdesEligibility = undefined)
    });
  }

  // L'emplacement FDES affiche la génération la plus récente ; l'historique est déjà trié du plus récent
  // au plus ancien côté serveur.
  loadLatestFdes(): void {
    this.latestFdes = undefined;
    this.fdesHistoryLoaded = false;
    if (!this.canViewFdes || !this.currentItem?.resourceId) {
      return;
    }
    this.fdesService.getHistory(this.currentItem.resourceId).subscribe({
      next: (rows) => {
        this.latestFdes = rows?.[0];
        this.fdesHistoryLoaded = true;
      },
      error: () => {
        this.latestFdes = undefined;
        this.fdesHistoryLoaded = true;
      }
    });
  }

  downloadFdes(): void {
    if (!this.latestFdes || !this.currentItem?.resourceId) {
      return;
    }
    const kind = fdesDocumentKind(this.latestFdes);
    if (!kind) {
      return;
    }
    this.fdesService.downloadFile(this.currentItem.resourceId, this.latestFdes.fdesGenerationId, kind).subscribe({
      next: (response) => saveFdesFile(response, this.fdesFileName)
    });
  }

  // Nom présenté à l'utilisateur, aligné sur celui que sert le serveur.
  get fdesFileName(): string {
    const date = this.latestFdes?.generatedDate ? new Date(this.latestFdes.generatedDate) : undefined;
    const stamp = date ? `${date.getFullYear()}${`${date.getMonth() + 1}`.padStart(2, '0')}${`${date.getDate()}`.padStart(2, '0')}` : '';
    const name = (this.currentItem?.displayName || '').replace(/[^\w-]+/g, '_').replace(/_+/g, '_');
    const extension = this.latestFdes?.pdfRelativePath ? 'pdf' : 'docx';
    return `FDES_${name}_${stamp}.${extension}`;
  }

  form!: FormGroup;
  families!: Family[];
  unitys!: Unity[];
  perimeterId!: string;
  transports!: Transport[];
  energies!: Energie[];
  industries!: Industrie[];
  hasDensity: boolean = false;
  get hasCombustibles(): boolean {
    return !!this.currentItem?.combustibles?.length;
  }
  // Une usine d'enrobé passe par la formule thermique, qui lit la température : sans elle, le poste
  // fabrication serait calculé à 0 °C.
  private readonly makingTemperatureRequired = (control: AbstractControl) => requiredIfCondition(this.hasCombustibles, control, 'Température de fabrication (°C)');
  WorkUnityCode = WorkUnityCode;
  listIndustries!: Industrie[];
  isHygrometryRateVisible!: boolean;
  formSubmitted?: boolean;
  compositionTouched?: boolean;
  manageProducts!: boolean;
  manageProductsEcoPro!: boolean;
  favoriteId?: string;
  unSaved: boolean = false;
  showErrorMessage?: boolean;
  get displayedColumns(): string[] {
    return this.usesPlantMix ? ['combustibleDisplayName', 'combustibleCode', 'value'] : ['combustibleDisplayName', 'combustibleCode', 'value', 'edit'];
  }
  bondingLayerProducts?: Product[];
  fileToUpload?: File;
  justificationFilePath: string;
  versionId: string = '';
  dbVersions: Version[] = [];

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;
  @ViewChild('resourceSection', { static: false })
  resourceSection!: ElementRef;

  projectTypes!: ProjectType[];

  constructor(
    private productFormulaService: ProductFormulaService,
    public dialog: MatDialog,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private transportService: TransportService,
    private energieService: EnergieService,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    private acheminementService: AcheminementService,
    private familyService: FamilyService,
    private industrieService: IndustrieService,
    private authService: AuthService,
    private utilsService: UtilsService,
    private spinnerService: SpinnerOverlayService,
    private uploadService: UploadService,
    private versionService: VersionService,
    private projectTypeService: ProjectTypeService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService,
    private fdesService: FdesService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';
  }

  async canDeactivate(): Promise<boolean> {
    if (this.unSaved) {
      const dialogRef = this.dialog.open(DialogLeaveComponent);

      var rep = await dialogRef.afterClosed().toPromise();
      if (rep) {
        return true;
      }

      return false;
    }
    return true;
  }

  async ngOnInit() {
    this.showErrorMessage = false;
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.form = this.formBuilder.group(
      {
        resourceId: [],
        code: ['', Validators.required],
        displayName: ['', Validators.required],
        owner: [{ value: '', disabled: true }],
        ownerId: [''],
        scopeId: [''],
        creator: [{ value: '', disabled: true }],
        density: ['', (control: AbstractControl) => requiredIfCondition(this.hasDensity, control, 'Densité (t/UO)*') ?? positiveIfCondition(this.hasDensity, control)],
        hygrometryRate: [0, Validators.max(100)],
        familyId: ['', Validators.required],
        description: [''],
        workUnityId: ['', Validators.required],
        industryId: ['', Validators.required],
        // Bornes posées par familyChange : elles ne valent que pour les enrobés bitumineux.
        makingTemperature: [null, this.makingTemperatureRequired],
        status: [],
        ceu: this.formBuilder.group({
          en: [],
          co: [],
          coString: [],
          gn: [],
          ae: [],
          mr: [],
          drc: []
        }),
        fileName: [],
        fileDescription: [],
        roadProject: [],
        earthworkProject: [],
        ratioProducts: [100, Validators.compose([Validators.min(100), Validators.max(100)])],
        ratioCombustibles: [100, Validators.compose([Validators.min(100), Validators.max(100)])],
        projectType: [[], validateProjectTypeRequired('validation_project_type_required')],
        versionId: ['']
      }
      //{ validators: [atLeastOne('roadProject', 'earthworkProject')] }
      // requiredIf('industryId', 'makingTemperature')
      // { validators: [atLeastOne('roadProject', 'earthworkProject'), isRatio100('ratioProducts'), isRatio100('ratioCombustibles')] }
    );

    await this.loadLists();

    this.versionService.getAll().subscribe((versions) => {
      this.dbVersions = versions;
      if (versions?.length > 0) {
        this.loadVersionResources(versions[0].versionId);
      }

      this.activeRoute.params.subscribe((params) => {
        const id = params['id'];
        const resourceId = id === 'new' || id === '0' ? undefined : id;
        this.loadFormula(resourceId);
        this.unSaved = false;
      });
    });
  }

  loadFormula(resourceId?: string) {
    if (resourceId)
      this.productFormulaService.get(resourceId, this.dbVersions?.[0]?.versionId).subscribe(
        (item) => {
          this.initForm(this.form, item);
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          this.router.navigate([SiteMapUrl.MenuProductFormula]);
        }
      );
    else this.initForm(this.form, {});
  }

  async loadLists() {
    this.industries = await this.industrieService.getIndustriesFormula().toPromise();
    const allFamilies = await this.familyService.getRessourceByTypeCode(ResourceTypeCodes.Produit).toPromise();
    this.families = allFamilies?.filter((x: Family) => x.modality !== ProductFamilyType.Other.toString()) || [];
    this.unitys = await this.productFormulaService.getUnity().toPromise();
    this.projectTypes = await this.projectTypeService.getAll().toPromise();
  }

  checkFormulaErrors() {
    this.showErrorMessage = this.currentItem.produits?.some((o) => o.resourceNotAvailableForThisVersion || (o.transportId && o.transportationNotAvailableForThisVersion));
  }

  ngDoCheck(): void {
    if (this.authService?.accessesValues) {
      this.manageProducts = this.authService.accessesValues.includes(FunctionalityCode.ManageProducts);
      this.manageProductsEcoPro = this.authService.accessesValues.includes(FunctionalityCode.ManageProductEcoPro);
    }
  }

  // Initialiser le formulaire
  initForm(form: FormGroup, currentItem: Formula) {
    if (this.families && this.families[0]) {
      this.familyChange(currentItem?.familyId);
    }
    this.currentItem = currentItem;
    this.resolveFdesEligibility();

    if (this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });
    }

    form.reset();
    form.patchValue(currentItem);

    this.loadedIndustryId = currentItem.industryId;
    if (currentItem.industryId) {
      this.repriseIndustryMix(currentItem.industryId);
    } else {
      this.calculTotalRatioCombustibles(currentItem.combustibles);
    }
    this.calculTotalRatioProducts(currentItem.produits);
    this.changeWorkUnity(currentItem.workUnityId!, this.unitys);

    if (this.dbVersions?.[0]) {
      this.form.patchValue({
        versionId: this.dbVersions[0].versionId
      });
    }
    this.unSaved = false;

    this.form.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => {
          if (!this.manageProducts || !(currentItem.canUpdate || !currentItem.resourceId)) {
            // Remove versionId and ceu from comparison
            const { versionId: _, ceu: __, ...prevRest } = prev;
            const { versionId: ___, ceu: ____, ...currRest } = curr;

            return JSON.stringify(prevRest) === JSON.stringify(currRest);
          }

          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe(() => {
        this.unSaved = true;
      });
  }

  get formCrls() {
    return this.form.controls;
  }

  private loadVersionResources(versionId?: string) {
    this.transportService.getAll(undefined, versionId).subscribe((data) => (this.transports = data));
    this.energieService.getAllForTransportation(undefined, versionId).subscribe((data) => (this.energies = data));
  }

  onVersionChange(value: string) {
    this.loadVersionResources(value);

    if (!this.currentItem.resourceId) return;

    this.productFormulaService.get(this.currentItem.resourceId, value).subscribe((itm) => {
      this.setFormCeu(itm.ceu);

      if (itm.produits) {
        itm.produits?.forEach((updateProduct: any) => {
          if (updateProduct.produitId) {
            let productToUpdate = this.currentItem.produits?.find((product) => product.produitId == updateProduct.produitId);
            if (productToUpdate) {
              productToUpdate.resourceNotAvailableForThisVersion = updateProduct.resourceNotAvailableForThisVersion;
              productToUpdate.transportationNotAvailableForThisVersion = updateProduct.transportationNotAvailableForThisVersion;
            }
          }
        });
        this.checkFormulaErrors();
      }
    });
  }

  private resetToLatestVersion(ceu: any) {
    const latestVersionId = this.dbVersions?.[0]?.versionId;
    if (!latestVersionId) return;

    const versionChanged = this.formCrls.versionId.value !== latestVersionId;

    this.form.patchValue({ versionId: latestVersionId });

    if (ceu && Object.keys(ceu).length) this.setFormCeu(ceu);

    if (versionChanged) this.loadVersionResources(latestVersionId);

    this.checkFormulaErrors();

    this.unSaved = false;
  }

  setFormCeu(ceu: any) {
    let isEmpty: boolean = Object.keys(ceu).length == 0;
    if (isEmpty) {
      this.snackbarService.open(SnackbarTypes.ResourceNotAvailableForversion);
      /*this.snackbarService.showMessage(
        "La ressource n'existe pas dans cette version."
      );*/
    }

    this.form.patchValue({
      ceu: {
        en: isEmpty ? '' : ceu.en,
        co: isEmpty ? '' : ceu.co,
        coString: isEmpty ? '' : ceu.coString,
        gn: isEmpty ? '' : ceu.gn,
        ae: isEmpty ? '' : ceu.ae,
        mr: isEmpty ? '' : ceu.mr,
        drc: isEmpty ? '' : ceu.drc
      }
    });
  }

  onDuplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 15, duplicate: (code: string) => this.productFormulaService.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res: any) => {
        if (res) {
          this.majStore(SnackbarTypes.duplicate);
          this.router.navigate([SiteMapUrl.MenuFormula, res.resourceId]);
        }
      });
  }

  onShare(resourceId: string) {
    var eusToShare: UserEntity[] = [];

    this.productFormulaService.getEUsToShareFormula(resourceId).subscribe((data) => {
      const dialogRef = this.dialog.open(DialogSelectEuShareComponent, {
        data: data
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          data.forEach((el: any) => {
            eusToShare?.push({
              displayName: el?.displayName,
              euId: el?.resourceId
            });
          });
          this.productFormulaService.publishFormula(resourceId, eusToShare).subscribe(
            () => {
              this.snackbarService.open(SnackbarTypes.share);
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
              this.spinnerService.hide();
            }
          );
        }
      });
    });
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }
  // Enregister une formule
  async clickSave(formula: Formula): Promise<boolean> {
    this.unSaved = false;

    this.formSubmitted = true;
    this.formCrls.makingTemperature.updateValueAndValidity();
    this.formCrls.density.updateValueAndValidity();

    if (this.form.invalid || !formula.produits || formula.produits.length == 0) {
      this.findInvalidControls();
      this.form.markAllAsTouched();
      this.snackbarService.showMessage(this.translate.instant(this.getSaveBlockingErrorKey(formula)));
      this.scrollToFirstInvalid();
      return false;
    } else {
      this.spinnerService.show();
    }

    let saved = Promise.resolve(false);
    try {
      formula = Object.assign(formula, this.form.value);
      if (this.usesPlantMix && formula.resourceId && this.storedCombustibles && formula.industryId === this.storedCombustibles.industryId) {
        formula.combustibles = this.storedCombustibles.combustibles;
      }

      if (!this.hasDensity) {
        delete formula.density;
      }

      // INSERT
      if (!this.formCrls?.resourceId?.value) {
        var versionId = this.currentItem.ruade ? this.formCrls.versionId.value : undefined;
        delete formula.resourceId;
        let rep = this.productFormulaService.addFormula(formula, versionId);
        rep.subscribe(
          (data: Formula) => {
            this.currentItem = data;
            this.majStore(SnackbarTypes.add);
            this.router.navigate([SiteMapUrl.MenuFormula, data.resourceId]);
          },
          (error) => {
            this.snackbarService.openError(error);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
      }
      // UPDATE
      else {
        formula.versionId = this.currentItem.ruade ? this.formCrls.versionId.value : undefined;

        let rep = this.productFormulaService.updateFormula(formula, formula.versionId);
        saved = new Promise<boolean>((resolve) =>
          rep.subscribe(
            (data: Formula) => {
              this.currentItem = data;
              if (!formula.versionId) this.resetToLatestVersion(data.ceu);
              this.majStore(SnackbarTypes.update);
              resolve(true);
            },
            (error) => {
              this.snackbarService.openError(error);
              this.spinnerService.hide();
              resolve(false);
            },
            () => {
              this.spinnerService.hide();
            }
          )
        );
      }
    } catch (ex) {
      this.snackbarService.open(SnackbarTypes.error);
      this.spinnerService.hide();
    }

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    return saved;
  }

  // Mise a jour de la liste des produits et formules
  majStore(snackbarMsgType: string) {
    this.store.select(StoreSelect.RESOURCES).subscribe(
      (data) => {
        this.store.dispatch(new UpdateResourceAction(data));
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      },
      () => {
        this.spinnerService.hide();
      }
    );

    this.productFormulaService.getAll().subscribe(
      (arr) => {
        if (arr) {
          this.spinnerService.hide();
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      },
      () => {
        this.spinnerService.hide();
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  // Pop-up de suppression
  onDeleteDialog(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_formula') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.currentItem.canUnlink) {
          this.productFormulaService.unlink(resourceId).subscribe(
            () => {
              this.majStore(SnackbarTypes.delete);
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
              this.spinnerService.hide();
            }
          );
        } else {
          this.deleteFormula(resourceId);
        }
      }
    });
  }

  // Supprimer une formule
  deleteFormula(resourceId: string) {
    if (resourceId) {
      this.productFormulaService.delete(resourceId).subscribe({
        next: (data) => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuProductFormula]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  // Ajouter un nouveau produit dans la composition
  addProduit(formula: Formula) {
    var operationtype: string;
    operationtype = '';
    formula = Object.assign(formula, this.form.value);

    if (formula.roadProject != null && formula.earthworkProject != null && formula.roadProject && formula.earthworkProject) operationtype = '';
    else if (!formula.roadProject && formula.earthworkProject) {
      operationtype = 'earthwork';
    } else if (formula.roadProject && !formula.earthworkProject) {
      operationtype = 'ROAD';
    }

    const versionId = this.form.get('versionId')?.value;

    this.productFormulaService.getAll(undefined, operationtype, versionId).subscribe((data) => {
      this.productFormulaService.getAll(GroupBy.favoriteDialog).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((data) => {
          if (data) {
            if (!this.currentItem.produits) {
              this.currentItem.produits = [];
            }

            data.forEach((el: any) => {
              this.currentItem.produits?.push({
                code: el?.code,
                produitId: el?.resourceId,
                displayName: el?.displayName,
                distance: 0,
                ratio: 0,
                ownerId: el?.ownerId,
                ruade: el?.ruade,
                fileName: el?.fileName,
                fCeuFileName: el?.fCeuFileName
              });
            });

            this.calculTotalRatioProducts(this.currentItem.produits);
            this.unSaved = true;
          }
        });
      });
    });
  }

  // Ajouter un transport multimodal
  addMultimodal(item: FormulaComposition) {
    this.acheminementService.getAll().subscribe((data) => {
      this.acheminementService.getAll(GroupBy.favoriteDialog).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((data) => {
          if (data && data[0]) {
            let fst = data[0];
            item.code = fst?.code;
            item.transportId = fst?.resourceId;
            item.transportName = fst?.displayName;
            item.isMultimodal = true;
            this.unSaved = true;
          }
        });
      });
    });
  }

  // Enlever un transport multimodal
  deleteMultimodal(item: FormulaComposition) {
    if (item) {
      item.transportId = undefined;
      item.transportName = undefined;
      item.isMultimodal = false;
      this.unSaved = true;
    }
  }

  deleteProduit(index: number) {
    this.compositionTouched = true;

    if (this.currentItem?.produits) this.currentItem.produits.splice(index, 1);
    this.calculTotalRatioProducts(this.currentItem.produits);
    this.unSaved = true;
  }

  // Modifier un produit dans la composition
  editProduit(item: FormulaComposition) {
    const dialogRef = this.dialog.open(DialogEditCompositionComponent, {
      data: {
        produits: {
          distance: item.distance,
          doubleCargo: item.doubleCargo,
          ratio: item.ratio,
          transportId: item.transportId,
          transportName: item.transportName,
          energyId: item?.energyId,
          energyName: item?.energyName
        },
        transports: this.transports,
        energies: this.energies
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        item.transportId = data?.transportId;
        item.transportName = data?.transportName;
        item.energyId = data?.energyId;
        item.energyName = data?.energyName;
        item.ratio = data?.ratio;
        item.doubleCargo = data?.doubleCargo;
        item.distance = data?.distance;
        item.transportDeleted = false;
        this.unSaved = true;
        item.transportationNotAvailableForThisVersion = false;
      }
      this.calculTotalRatioProducts(this.currentItem.produits);
      this.checkFormulaErrors();
    });
  }

  // Modifier uniquement le ratio
  editRatio(item: FormulaComposition) {
    const dialogRef = this.dialog.open(DialogEditRatioComponent, {
      data: {
        ratio: item.ratio
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data != null) {
        item.ratio = data;
        this.unSaved = true;
      }
      this.calculTotalRatioProducts(this.currentItem.produits);
    });
  }

  // Calculer le ratio total pour la composition des produits
  calculTotalRatioProducts(produits?: any[]): number {
    var result;
    let obj: any;
    if (produits && produits.length > 0) {
      obj = produits?.reduce((a: any, b: any) => {
        result = parseFloat(a.ratio) + parseFloat(b.ratio);
        return { ratio: parseFloat(result.toFixed(8)) };
      });
      this.form.patchValue({ ratioProducts: obj?.ratio || 0 });
    } else {
      this.form.patchValue({ ratioProducts: 100 });
    }
    return obj?.ratio || 0;
  }

  private getSaveBlockingErrorKey(formula: Formula): string {
    if (this.formCrls.density.errors?.required) return 'formula_error_density_required';
    if (this.formCrls.density.errors?.positive) return 'formula_error_density_positive';
    if (this.formCrls.ratioCombustibles.invalid) return 'formula_error_combustibles_ratio_100';
    if (this.formCrls.makingTemperature.errors?.min) return 'formula_error_making_temperature_min';
    if (this.formCrls.makingTemperature.errors?.max) return 'formula_error_making_temperature_max';
    if (this.formCrls.makingTemperature.invalid) return 'formula_error_making_temperature_required';
    if (!formula.produits || formula.produits.length == 0) return 'formula_error_composition_required';
    if (this.formCrls.ratioProducts.invalid) return 'formula_error_composition_ratio_100';
    return 'error_fill_required_fields';
  }

  // Calculer le ratio total pour les combustibles
  calculTotalRatioCombustibles(combustibles?: any[]): number {
    let obj: any;
    if (combustibles && combustibles.length > 0) {
      obj = combustibles?.reduce((a: any, b: any) => {
        return { value: parseFloat(a.value) + parseFloat(b.value) };
      });
      this.form.patchValue({ ratioCombustibles: obj?.value || 0 });
    } else {
      this.form.patchValue({ ratioCombustibles: 100 });
    }
    this.form.get('makingTemperature')?.updateValueAndValidity();
    return obj?.value || 0;
  }

  // Lors du changement de la densité
  changeWorkUnity(workUnityId: string, unitys: Unity[]) {
    if (workUnityId && unitys.length > 0) {
      let shortName = unitys.find((x) => x.workUnityId == workUnityId)?.shortDisplayName;
      if (shortName != WorkUnityCode.Ton) {
        this.hasDensity = true;
        this.formCrls.density.updateValueAndValidity();
        return;
      }
    }

    this.hasDensity = false;
    this.form.patchValue({ density: null });
  }

  // Lors du changement de la famille dans la liste
  familyChange(id?: string) {
    var currentFamily = id ? this.families?.find((x) => x.familyId == id) : undefined;

    // La formule thermique RdF n'est valable qu'entre 65 et 180 °C, et elle ne couvre que les enrobés
    // bitumineux. Sur les autres familles la température vaut souvent 0 et le champ est masqué : la
    // borne rendait la formule non enregistrable sans qu'aucun message ne s'affiche.
    const makingTemperature = this.form.get('makingTemperature');
    makingTemperature?.setValidators([this.makingTemperatureRequired, ...(currentFamily?.code == 'EB' ? [Validators.min(65), Validators.max(180)] : [])]);
    makingTemperature?.updateValueAndValidity({ emitEvent: false });

    if (!currentFamily) {
      return;
    }

    // Une famille de formule n'accepte que les industries de la famille d'usine correspondante :
    // bétons -> unité de production de béton, MTLH -> usine de MTLH, enrobés bitumineux -> usine
    // d'enrobés, enrobés à froid -> usine d'enrobés à l'émulsion, émulsion et bitume modifié ->
    // l'atelier de formulation du même nom.
    let codeFilter: (string | undefined)[] = [];
    if (currentFamily.code == 'B') codeFilter = ['CEBN'];
    if (currentFamily.code == 'MTLH') codeFilter = ['CEB'];
    if (currentFamily.code == 'EB') codeFilter = ['CEE'];
    if (currentFamily.code == 'EF') codeFilter = ['CEF'];
    if (currentFamily.code == 'FDE') codeFilter = ['FDE'];
    if (currentFamily.code == 'FBM') codeFilter = ['FBM'];

    this.isHygrometryRateVisible = currentFamily.hygrometry ? true : false;
    this.listIndustries = this.industries ?? [];
    if (codeFilter.length > 0) {
      this.listIndustries = this.listIndustries.filter((i) => codeFilter.includes(i.familyCode));
    }

    const selectedIndustryId = this.form?.get('industryId')?.value;
    if (selectedIndustryId && !this.listIndustries.some((i) => i.resourceId == selectedIndustryId)) {
      this.form.get('industryId')?.reset();
    }

    if (this.currentItem && this.currentItem.combustibles) {
      this.currentItem.combustibles = [];
    }
  }

  industrieChange(industryId: string) {
    this.repriseIndustryMix(industryId);
  }

  // Avec la formule RdF 2025, le mix combustible est porté par l'usine : la formule l'affiche en lecture seule, et
  // n'enregistre une copie que lorsqu'elle est créée ou change d'usine. Son propre % reste celui de l'ancien calcul.
  private repriseIndustryMix(industryId: string) {
    const formulaId = this.currentItem?.resourceId ?? '';
    const versionId = this.currentItem?.ruade ? this.formCrls.versionId.value : undefined;
    forkJoin({
      combustibles: this.productFormulaService.getCombustibles(formulaId, industryId),
      usesPlantMix: this.industrieService.isPlantMixInUse(versionId)
    })
      .pipe(switchMap(({ combustibles, usesPlantMix }) => (usesPlantMix ? this.loadEnergyProfile(industryId) : of(null)).pipe(map((profile) => ({ combustibles, usesPlantMix, profile })))))
      .subscribe(({ combustibles, usesPlantMix, profile }) => {
        this.energyProfile = profile;
        this.usesPlantMix = usesPlantMix && profile != null;
        this.storedCombustibles = formulaId && industryId === this.loadedIndustryId ? { industryId, combustibles: combustibles || [] } : undefined;
        if (this.usesPlantMix) {
          const mixByCode = new Map<string, number>();
          (profile?.combustibles || []).forEach((c) => {
            if (c.code != null) mixByCode.set(c.code, c.usagePct ?? 0);
          });
          this.currentItem.combustibles = (combustibles || []).map((c: any) => ({
            ...c,
            value: String(mixByCode.get(c.combustibleCode ?? '') ?? 0)
          }));
        } else {
          this.currentItem.combustibles = combustibles || [];
        }
        const ratio = this.form.get('ratioCombustibles');
        ratio?.setValidators(this.usesPlantMix ? null : Validators.compose([Validators.min(100), Validators.max(100)]));
        this.calculTotalRatioCombustibles(this.currentItem.combustibles);
        ratio?.updateValueAndValidity();
      });
  }

  // Seule une usine d'enrobé a un profil énergétique. S'il ne peut pas être lu, les combustibles restent
  // affichés et l'utilisateur voit pourquoi, au lieu d'une page amputée sans explication.
  private loadEnergyProfile(industryId: string): Observable<EnergyProfile | null> {
    const industry = this.listIndustries?.find((i) => i.resourceId == industryId);
    if (industry?.familyCode !== FamilyCodes.CoatedIndustry) return of(null);
    return this.industrieService.getEnergyProfile(industryId).pipe(
      catchError((error) => {
        this.snackbarService.showMessage(error);
        return of(null);
      })
    );
  }

  // Modifier la valeur en pourcentage d'un combustible
  editValueCombustible(cmb: CombustibleFormula) {
    const dialogRef = this.dialog.open(DialogEditCombustibleFormulaComponent, {
      data: { value: cmb?.value }
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value != undefined && value >= 0) {
        cmb.value = value.toString();
        this.calculTotalRatioCombustibles(this.currentItem.combustibles);
      }
    });
  }

  calculCEU() {
    this.dialog.open(DialogDisplayCeuComponent, {
      data: { formula: this.currentItem }
    });
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }

  editProduct(item: FormulaComposition) {
    this.productFormulaService.getAll(GroupBy.family).subscribe((data) => {
      this.productFormulaService.getAll(GroupBy.favorite).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: this.filterCurrentFormula(data),
            favoriteData: this.filterCurrentFormula(favoriteData),
            isSingular: true,
            editMode: true
          }
        });

        dialogRef.afterClosed().subscribe(async (selectedProducts: any[]) => {
          const newProduct = selectedProducts?.[0];
          if (newProduct) {
            if (item.resourceCompositionId) {
              this.productFormulaService
                .updateFormulaComposition(item.resourceCompositionId, newProduct.resourceId)
                .toPromise()
                .then(() => this.snackbarService.open(SnackbarTypes.compositionUpdate))
                .catch(() => this.snackbarService.open(SnackbarTypes.error));
            }
            item.produitId = newProduct.resourceId;
            item.displayName = newProduct.displayName;
            item.code = newProduct.code;
            item.ownerId = newProduct.ownerId;
            item.ruade = newProduct.ruade;
            item.fileName = newProduct.fileName;
            item.fCeuFileName = newProduct.fCeuFileName;
          }
        });
      });
    });
  }

  filterCurrentFormula(products: any[]) {
    products.forEach((p) => {
      if (p.value) {
        p.value = this.filterCurrentFormula(p.value);
        if (p.value.length == 0) {
          products = products.filter((pr) => pr != p);
        }
      }
    });

    return products.filter((p) => p.resourceId != this.currentItem.resourceId);
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType) {
    return compare(pt1, pt2);
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId || !files || files.length === 0) {
      return;
    }

    this.fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          this.fileInput.nativeElement.value = '';
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
  }

  private scrollToBottom(): void {
    if (this.resourceSection?.nativeElement) {
      this.resourceSection.nativeElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }

  // bring the first invalid (now red) field into view after a failed save
  private scrollToFirstInvalid(): void {
    setTimeout(() => {
      const el = document.querySelector('.mat-form-field-invalid') as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.scrollToBottom();
    }, 60);
  }
}
